import { useEffect, useState, useRef } from 'react';
import styles from './SuccessStep.module.css';
import { useCheckoutStore } from '../store/checkout';
import { getMarketConfig } from '../config/market';

interface SuccessStepProps {
  successUrl?: string;
  sessionId?: string;
  /** The shop's own order number, so the thank-you page need not look it up. */
  reference?: string;
  orderId?: string;
  paymentId?: string;
}

export function SuccessStep({ successUrl, sessionId, orderId, paymentId, reference }: SuccessStepProps) {
  const [showCheckmark, setShowCheckmark] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const paymentResult = useCheckoutStore((s) => s.paymentResult);
  const customer = useCheckoutStore((s) => s.customer);
  const selectedShipping = useCheckoutStore((s) => s.selectedShipping);
  const cart = useCheckoutStore((s) => s.cart);
  const locale = useCheckoutStore((s) => s.locale);
  const market = getMarketConfig(locale);
  const L = market.labels;
  const redirectTriggered = useRef(false);

  // Build redirect URL with query params
  const buildRedirectUrl = () => {
    if (!successUrl) return null;
    
    try {
      const url = new URL(successUrl);
      if (sessionId) url.searchParams.set('sessionId', sessionId);
      if (orderId) url.searchParams.set('orderId', orderId);
      if (paymentId) url.searchParams.set('paymentId', paymentId);
      if (reference) url.searchParams.set('reference', reference);
      url.searchParams.set('status', 'success');
      return url.toString();
    } catch {
      // If URL parsing fails, append params manually
      const separator = successUrl.includes('?') ? '&' : '?';
      const params = new URLSearchParams();
      if (sessionId) params.set('sessionId', sessionId);
      if (orderId) params.set('orderId', orderId);
      if (paymentId) params.set('paymentId', paymentId);
      if (reference) params.set('reference', reference);
      params.set('status', 'success');
      return `${successUrl}${separator}${params.toString()}`;
    }
  };

  // Animation sequence
  useEffect(() => {
    // First: show checkmark with animation
    const timer1 = setTimeout(() => setShowCheckmark(true), 100);
    // Then: show content
    const timer2 = setTimeout(() => setShowContent(true), 800);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  // Countdown and redirect
  useEffect(() => {
    if (!successUrl || redirectTriggered.current) return;
    
    const countdownTimer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownTimer);
          if (!redirectTriggered.current) {
            redirectTriggered.current = true;
            const redirectUrl = buildRedirectUrl();
            if (redirectUrl) {
              window.location.href = redirectUrl;
            }
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownTimer);
  }, [successUrl, sessionId, orderId, paymentId, reference]);

  const handleManualRedirect = () => {
    if (redirectTriggered.current) return;
    redirectTriggered.current = true;
    const redirectUrl = buildRedirectUrl();
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  const totalAmount = (cart?.totalAmount?.amount || 0) + (selectedShipping?.price?.amount || 0);
  const currency = cart?.totalAmount?.currency || 'NOK';

  return (
    <div className={styles.container}>
      {/* Animated checkmark circle */}
      <div className={`${styles.checkmarkContainer} ${showCheckmark ? styles.show : ''}`}>
        <svg className={styles.checkmark} viewBox="0 0 52 52">
          <circle 
            className={styles.checkmarkCircle} 
            cx="26" 
            cy="26" 
            r="25" 
            fill="none"
          />
          <path 
            className={styles.checkmarkCheck} 
            fill="none" 
            d="M14.1 27.2l7.1 7.2 16.7-16.8"
          />
        </svg>
      </div>

      {/* Content */}
      <div className={`${styles.content} ${showContent ? styles.show : ''}`}>
        <h1 className={styles.title}>{L.paymentCompleted}</h1>
        <p className={styles.subtitle}>{L.thankYouMessage}</p>

        {/* Order summary */}
        <div className={styles.summary}>
          <div className={styles.summaryRow}>
            <span>{L.amountPaid}</span>
            <span className={styles.amount}>
              {(totalAmount / 100).toLocaleString(market.sdkLocale === 'no' ? 'nb-NO' : market.sdkLocale === 'sv' ? 'sv-SE' : market.sdkLocale === 'da' ? 'da-DK' : market.sdkLocale === 'fi' ? 'fi-FI' : 'en-US', { 
                minimumFractionDigits: 2,
                maximumFractionDigits: 2 
              })} {currency}
            </span>
          </div>
          
          {customer?.email && (
            <div className={styles.summaryRow}>
              <span>{L.receiptSentTo}</span>
              <span>{customer.email}</span>
            </div>
          )}

          {paymentResult?.paymentMethod && (
            <div className={styles.summaryRow}>
              <span>{L.paymentMethodLabel}</span>
              <span className={styles.paymentMethod}>
                {paymentResult.paymentMethod === 'SVIPPS' ? 'Vipps' : 
                 paymentResult.paymentMethod === 'CPOC' ? L.cardLabel :
                 paymentResult.paymentMethod === 'KLARNA' ? 'Klarna' :
                 paymentResult.paymentMethod === 'NSWISH' ? 'Swish' :
                 paymentResult.paymentMethod === 'SSWISH' ? 'Swish' :
                 paymentResult.paymentMethod}
              </span>
            </div>
          )}

          {orderId && (
            <div className={styles.summaryRow}>
              <span>{L.orderIdLabel}</span>
              <span className={styles.orderId}>{orderId}</span>
            </div>
          )}
        </div>

        {/* Redirect info */}
        {successUrl && (
          <div className={styles.redirectInfo}>
            <p>{L.redirectCountdown} <strong>{countdown}</strong> {locale === 'sv' ? 'sekunder...' : locale === 'da' ? 'sekunder...' : locale === 'fi' ? 'sekuntia...' : locale === 'en' ? 'seconds...' : 'sekunder...'}</p>
            <button className={styles.continueButton} onClick={handleManualRedirect}>
              {L.continueToStore}
            </button>
          </div>
        )}

        {!successUrl && (
          <div className={styles.noRedirect}>
            <p>{L.paymentRegistered}</p>
            <p className={styles.hint}>{L.closeWindowHint}</p>
          </div>
        )}
      </div>
    </div>
  );
}
